import React, { FC, ReactNode } from 'react'
import { format } from 'date-fns'
import { motion } from 'framer-motion'
import { slugs } from '@/constants/slugs'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import {
  GEyebrowLG,
  GEyebrowXL,
  GResponsiveText,
  GTitleLG,
  GTitleMD,
  GTitleSM,
  GTitleXL,
} from '@/views/PreSales/PreSalesGiveawayView/components/Typography'
import { WeeklyCountdownTimer } from '@/views/PreSales/PreSalesGiveawayView/components/WeeklyCountdownTimer'
import { useGiveawaySchedule } from '@/views/PreSales/PreSalesGiveawayView/hooks/useGiveawaySchedule'

interface Props {
  theatricalSlug: string
  winnerAnnouncedDate: Date
}

const DailyEntriesSlugs = new Set([slugs.kingOfKings, slugs.theLastRodeo])

export const GiveawayPromoBanner: FC<Props> = ({ theatricalSlug, winnerAnnouncedDate }) => {
  const { t } = useTranslate('tickets')

  const { normal, refetch, isActive, isReady, dailyBonusEntries, minRewardTicketQuantity } =
    useGiveawaySchedule(theatricalSlug)

  const multiplier = normal?.multiplier ?? 1

  if (!isReady) return null

  if (!isActive) {
    return (
      <YellowBannerWrapper>
        <GResponsiveText
          color="giveaway-text-primary-light"
          className="block"
          MobileTextComponent={GEyebrowLG}
          DesktopTextComponent={GEyebrowXL}
        >
          {t('giveawayHasEnded', 'Giveaway Has Ended')}
        </GResponsiveText>
        <GResponsiveText
          color="giveaway-text-primary-light"
          MobileTextComponent={GTitleSM}
          DesktopTextComponent={GTitleSM}
        >
          {t('winnerAnnouncedGiveaway', 'Winner Announced {{winnerDate}}', {
            winnerDate: format(winnerAnnouncedDate, 'MMMM d, yyyy'),
          })}
          *
        </GResponsiveText>
      </YellowBannerWrapper>
    )
  }

  return (
    <YellowBannerWrapper>
      {DailyEntriesSlugs.has(theatricalSlug) && (
        <>
          <GResponsiveText
            color="giveaway-text-primary-light"
            className="block"
            MobileTextComponent={GEyebrowLG}
            DesktopTextComponent={GEyebrowXL}
          >
            {t('todayOnly', 'Today Only')}
          </GResponsiveText>

          <GResponsiveText
            color="giveaway-text-primary-light"
            className="block"
            MobileTextComponent={GTitleLG}
            DesktopTextComponent={GTitleXL}
          >
            {t('getBonusEntriesGiveaway', 'Get {{dailyBonusEntries}} Bonus Entries', { dailyBonusEntries })}
          </GResponsiveText>

          <GResponsiveText
            color="giveaway-text-primary-light"
            className="block"
            MobileTextComponent={GEyebrowLG}
            DesktopTextComponent={GEyebrowXL}
          >
            {t('minRewardQuantity', 'When you buy {{minRewardTicketQuantity}}+ tickets', {
              minRewardTicketQuantity,
            })}
          </GResponsiveText>
        </>
      )}

      {!DailyEntriesSlugs.has(theatricalSlug) && (
        <>
          <GResponsiveText
            color="giveaway-text-primary-light"
            className="block"
            MobileTextComponent={GEyebrowLG}
            DesktopTextComponent={GEyebrowXL}
          >
            {t('thisWeekOnly', 'This week only')}
          </GResponsiveText>

          <GResponsiveText
            color="giveaway-text-primary-light"
            className="block !font-medium uppercase"
            MobileTextComponent={GTitleLG}
            DesktopTextComponent={GTitleXL}
          >
            <Translate t={t} i18nKey="getMultiplierYourEntires">
              Get <span className="!font-extrabold">{{ multiplier }}x</span> Your Entries
            </Translate>
          </GResponsiveText>

          <div className="flex justify-center gap-2 md:justify-start">
            <GResponsiveText
              color="giveaway-text-primary-light"
              className="!font-medium uppercase"
              MobileTextComponent={GEyebrowLG}
              DesktopTextComponent={GTitleMD}
            >
              {t('endingIn', 'Ending In')}
            </GResponsiveText>

            <WeeklyCountdownTimer
              theatricalSlug={theatricalSlug}
              onCountdownEnd={refetch}
              className="!homestead-eyebrow-lg"
            />
          </div>
        </>
      )}
    </YellowBannerWrapper>
  )
}

const containerVariants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
    },
  },
}

const YellowBannerWrapper = ({ children }: { children: ReactNode }) => {
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="-mx-6 bg-giveaway-secondary py-2.5 text-center md:mx-0 md:w-fit md:px-6 md:py-4 md:text-left"
    >
      {children}
    </motion.div>
  )
}
